import { useState, useRef } from 'react'
import { Box, Flex, Text, VStack, Grid, ConfirmDialog, toaster, useT } from '@minetrace/ui'
import { useFormColors } from '@minetrace/ui/forms'
import {
  LuChevronDown as ChevronDown,
  LuChevronRight as ChevronRight,
  LuFlame as Flame,
  LuPlus as Plus,
  LuPencil as Pencil,
  LuTrash2 as Trash2,
  LuDownload as Download,
  LuUpload as Upload,
  LuUsers as Users,
} from 'react-icons/lu'
import type { MiningRightResponse, ExplosiveAuthorizationResponse, ExplosiveHandlerResponse } from '../../../api/models/response/proveedor'
import type { AddExplosiveAuthorizationRequest, AddExplosiveHandlerRequest } from '../../../api/models/request/proveedor'
import { enumLabel, EXPLOSIVE_AUTHORIZATION_TYPES } from '../../../data/enums'
import AutorizacionModal from './AutorizacionModal'
import ManipuladorModal, { type ManipuladorLocal } from './ManipuladorModal'
import DocumentoDescarga from '../../../components/DocumentoDescarga'

interface Step7IqbfProps {
  mode: 'edit' | 'readonly'
  companyId: string | null
  savedMiningRights: MiningRightResponse[]
  onToggleUsesExplosives: (mrId: string, value: boolean) => Promise<void>
  onAddAuthorization: (mrId: string, data: AddExplosiveAuthorizationRequest, documentFile?: File) => Promise<void>
  onUpdateAuthorization: (mrId: string, authId: string, data: AddExplosiveAuthorizationRequest, documentFile?: File) => Promise<void>
  onRemoveAuthorization: (mrId: string, authId: string) => Promise<void>
  onAddHandler: (mrId: string, data: AddExplosiveHandlerRequest) => Promise<void>
  onBulkAddHandlers: (mrId: string, items: AddExplosiveHandlerRequest[]) => Promise<void>
  onRemoveHandler: (mrId: string, handlerId: string) => Promise<void>
}

// ── Parser CSV de carga masiva de manipuladores ──
// Columnas esperadas (con encabezado): dni, nombres, apellidoPaterno, apellidoMaterno, carne, vencimiento
function parseHandlersCsv(text: string): AddExplosiveHandlerRequest[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
  if (lines.length === 0) return []
  // Detecta y descarta encabezado si la primera celda no es numérica.
  const first = lines[0]!.split(/[,;]/)[0]!.trim()
  const rows = /^\d+$/.test(first) ? lines : lines.slice(1)
  return rows.map((line) => {
    const cols = line.split(/[,;]/).map((c) => c.trim())
    return {
      documentType: cols[0] && cols[0].length === 8 ? 1 : 2,
      identityDoc: cols[0] || undefined,
      names: cols[1] || undefined,
      paternalSurname: cols[2] || undefined,
      maternalSurname: cols[3] || undefined,
      licenseNumber: cols[4] || undefined,
      expirationDate: cols[5] || undefined,
      sucamecValidated: false,
    } as AddExplosiveHandlerRequest
  }).filter((r) => r.identityDoc)
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  valid: { bg: 'green.50', color: 'green.600' },
  expiringSoon: { bg: 'orange.50', color: 'orange.600' },
  expired: { bg: 'red.50', color: 'red.600' },
  unknown: { bg: 'gray.100', color: 'gray.600' },
}

export default function Step7Iqbf({
  mode, companyId, savedMiningRights,
  onToggleUsesExplosives, onAddAuthorization, onUpdateAuthorization, onRemoveAuthorization,
  onAddHandler, onBulkAddHandlers, onRemoveHandler,
}: Step7IqbfProps) {
  const c = useFormColors()
  const { t } = useT()
  const isReadonly = mode === 'readonly'

  const [expandedRights, setExpandedRights] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)

  // Modal de autorización
  const [authModal, setAuthModal] = useState<{ mrId: string; editing: ExplosiveAuthorizationResponse | null } | null>(null)
  // Modal de manipulador
  const [handlerModal, setHandlerModal] = useState<{ mrId: string } | null>(null)
  // Confirmaciones de borrado
  const [deleteAuth, setDeleteAuth] = useState<{ mrId: string; authId: string; name: string } | null>(null)
  const [deleteHandler, setDeleteHandler] = useState<{ mrId: string; handlerId: string; name: string } | null>(null)

  const bulkInputRef = useRef<HTMLInputElement>(null)
  const [bulkMrId, setBulkMrId] = useState<string | null>(null)

  const toggleRight = (id: string) => {
    setExpandedRights((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const handleToggleUses = async (mrId: string, value: boolean) => {
    setBusy(true)
    try {
      await onToggleUsesExplosives(mrId, value)
      if (value) setExpandedRights((prev) => new Set(prev).add(mrId))
    } finally { setBusy(false) }
  }

  const handleSaveAuth = async (data: AddExplosiveAuthorizationRequest, documentFile?: File) => {
    if (!authModal) return
    if (authModal.editing) {
      await onUpdateAuthorization(authModal.mrId, authModal.editing.id, data, documentFile)
    } else {
      await onAddAuthorization(authModal.mrId, data, documentFile)
    }
  }

  const handleSaveHandler = async (m: ManipuladorLocal) => {
    if (!handlerModal) return
    await onAddHandler(handlerModal.mrId, {
      personId: m.personId,
      licenseNumber: m.licenseNumber,
      expirationDate: m.expirationDate,
      sucamecValidated: m.sucamecValidated,
    })
  }

  // Descarga una plantilla CSV con el formato esperado por la carga masiva.
  const downloadTemplate = () => {
    const header = 'dni,nombres,apellidoPaterno,apellidoMaterno,carne,vencimiento'
    const example = '12345678,JUAN,PEREZ,GOMEZ,EXP-12345,2027-12-31'
    const blob = new Blob([`${header}\n${example}\n`], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'plantilla-manipuladores.csv'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const handleBulkFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !bulkMrId) return
    try {
      const text = await file.text()
      const parsed = parseHandlersCsv(text)
      if (parsed.length === 0) { toaster.error({ description: t('proveedores.wizard.step7.bulkEmpty') }); return }
      // No-duplicados: omite DNIs ya registrados en este Derecho Minero y repetidos
      // dentro del propio archivo. El backend lo valida también como respaldo.
      const existingDocs = new Set(
        (savedMiningRights.find((r) => r.id === bulkMrId)?.explosiveHandlers ?? [])
          .map((h) => (h.identityDoc ?? '').trim()).filter(Boolean),
      )
      const seen = new Set<string>()
      const items = parsed.filter((it) => {
        const doc = (it.identityDoc ?? '').trim()
        if (!doc || existingDocs.has(doc) || seen.has(doc)) return false
        seen.add(doc)
        return true
      })
      const skipped = parsed.length - items.length
      if (items.length === 0) { toaster.error({ description: t('proveedores.wizard.step7.bulkAllDuplicates') }); return }
      setBusy(true)
      await onBulkAddHandlers(bulkMrId, items)
      toaster.success({
        description: skipped > 0
          ? t('proveedores.wizard.step7.bulkSuccessWithSkipped', { count: items.length, skipped })
          : t('proveedores.wizard.step7.bulkSuccess', { count: items.length }),
      })
    } catch (err) {
      toaster.error({ description: err instanceof Error && err.message ? err.message : t('proveedores.wizard.step7.bulkError') })
    } finally {
      setBusy(false)
      setBulkMrId(null)
    }
  }

  const confirmDeleteAuth = async () => {
    if (!deleteAuth) return
    setBusy(true)
    try { await onRemoveAuthorization(deleteAuth.mrId, deleteAuth.authId); setDeleteAuth(null) } finally { setBusy(false) }
  }

  const confirmDeleteHandler = async () => {
    if (!deleteHandler) return
    setBusy(true)
    try { await onRemoveHandler(deleteHandler.mrId, deleteHandler.handlerId); setDeleteHandler(null) } finally { setBusy(false) }
  }

  return (
    <VStack gap={6} alignItems="stretch">
      <Flex alignItems="center" gap={2}>
        <Flame size={18} color={c.accent} />
        <Box>
          <Text tid="proveedores.wizard.step7.title" fontSize="md" fontWeight="700" color={c.text} />
          <Text tid="proveedores.wizard.step7.description" fontSize="sm" color={c.textMuted} />
        </Box>
      </Flex>

      {savedMiningRights.length === 0 ? (
        <Box p={8} textAlign="center" borderRadius="xl" border="1px dashed" borderColor="gray.200">
          <Flame size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
          <Text tid="proveedores.wizard.step7.emptyMessage" fontSize="sm" color={c.textMuted} />
        </Box>
      ) : (
        <VStack gap={4} alignItems="stretch">
          {savedMiningRights.map((right) => {
            const isExpanded = expandedRights.has(right.id)
            const auths = right.explosiveAuthorizations ?? []
            const handlers = right.explosiveHandlers ?? []
            const uses = right.usesExplosives

            return (
              <Box key={right.id} borderRadius="xl" border="1px solid" borderColor={c.border} overflow="hidden">
                {/* Header */}
                <Flex px={5} py={3} bg={c.bgHover} alignItems="center" justifyContent="space-between">
                  <Flex alignItems="center" gap={2} cursor="pointer" onClick={() => toggleRight(right.id)} flex={1}>
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <Text fontSize="sm" fontWeight="600" color={c.text}>{t('common.miningRightLabel')}: {right.name || right.uniqueCode}</Text>
                    <Text fontSize="xs" color={c.textMuted}>({right.uniqueCode})</Text>
                  </Flex>
                  {/* Switch ¿Utiliza explosivos? */}
                  <Flex alignItems="center" gap={2}>
                    <Text tid="proveedores.wizard.step7.usesExplosivesLabel" fontSize="xs" color={c.textMuted} />
                    <Box
                      as="button" type="button"
                      w="42px" h="24px" borderRadius="full" position="relative"
                      bg={uses ? c.accent : c.border}
                      cursor={isReadonly || busy ? 'not-allowed' : 'pointer'}
                      transition="background 0.2s"
                      onClick={() => { if (!isReadonly && !busy) handleToggleUses(right.id, !uses) }}
                    >
                      <Box
                        position="absolute" top="2px" left={uses ? '20px' : '2px'}
                        w="20px" h="20px" borderRadius="full" bg="white"
                        transition="left 0.2s" boxShadow="sm"
                      />
                    </Box>
                  </Flex>
                </Flex>

                {/* Cuerpo */}
                {isExpanded && (
                  <Box px={5} py={4}>
                    {!uses ? (
                      <Text tid="proveedores.wizard.step7.notUsingExplosives" fontSize="sm" color={c.textMuted} textAlign="center" py={4} />
                    ) : (
                      <>
                        {/* ── Autorizaciones ── */}
                        {!isReadonly && (
                          <Flex mb={3}>
                            <Box as="button" type="button" display="flex" alignItems="center" gap={2}
                              px={4} py={2} bg={c.accent} color="white" borderRadius="md" fontSize="sm" fontWeight="600"
                              cursor="pointer" _hover={{ opacity: 0.9 }} onClick={() => setAuthModal({ mrId: right.id, editing: null })}
                            >
                              <Plus size={14} />{t('proveedores.wizard.step7.addAuthorization')}
                            </Box>
                          </Flex>
                        )}

                        {auths.length > 0 ? (
                          <VStack gap={2} alignItems="stretch" mb={5}>
                            {auths.map((a) => (
                              <Box key={a.id} p={3} bg={c.bgInput} borderRadius="lg" border="1px solid" borderColor={c.border}>
                                <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={3}>
                                  <Box>
                                    <Text tid="proveedores.wizard.step7.authTypeCol" fontSize="xs" color={c.textMuted} />
                                    <Text fontSize="sm" color={c.text}>{a.authorizationTypeName ?? enumLabel(EXPLOSIVE_AUTHORIZATION_TYPES, a.authorizationType)}</Text>
                                  </Box>
                                  <Box>
                                    <Text tid="proveedores.wizard.step7.authResolutionCol" fontSize="xs" color={c.textMuted} />
                                    <Text fontSize="sm" color={c.text}>{a.resolutionNumber ?? '—'}</Text>
                                  </Box>
                                  <Box>
                                    <Text tid="proveedores.wizard.step7.authExpirationCol" fontSize="xs" color={c.textMuted} />
                                    <Text fontSize="sm" color={c.text}>{a.expirationDate ?? '—'}</Text>
                                  </Box>
                                  <Box>
                                    <Text tid="proveedores.wizard.step7.authDetailCol" fontSize="xs" color={c.textMuted} />
                                    <Text fontSize="sm" color={c.text}>{a.authorizedExplosives || a.maxCapacity || '—'}</Text>
                                  </Box>
                                </Grid>
                                <Flex mt={2} justifyContent="space-between" alignItems="center">
                                  {a.documentFile ? (
                                    <DocumentoDescarga caption="Autorización de explosivos" storagePath={a.documentFile.storagePath} fileName={a.documentFile.originalFileName} />
                                  ) : <Box />}
                                  {!isReadonly && (
                                    <Flex gap={2}>
                                      <Box as="button" type="button" display="flex" alignItems="center" gap={1} px={2.5} py={1} bg={`${c.accent}15`} color={c.accent} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer" _hover={{ bg: `${c.accent}25` }} onClick={() => setAuthModal({ mrId: right.id, editing: a })}>
                                        <Pencil size={11} />{t('common.edit')}
                                      </Box>
                                      <Box as="button" type="button" display="flex" alignItems="center" gap={1} px={2.5} py={1} bg="red.50" color="red.600" borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer" _hover={{ bg: 'red.100' }} onClick={() => setDeleteAuth({ mrId: right.id, authId: a.id, name: a.resolutionNumber ?? a.authorizationTypeName ?? '' })}>
                                        <Trash2 size={11} />{t('common.delete')}
                                      </Box>
                                    </Flex>
                                  )}
                                </Flex>
                              </Box>
                            ))}
                          </VStack>
                        ) : (
                          <Text tid="proveedores.wizard.step7.noAuthorizations" fontSize="sm" color={c.textMuted} textAlign="center" py={3} />
                        )}

                        {/* ── Manipuladores ── */}
                        <Flex alignItems="center" justifyContent="space-between" mb={3} pt={2} borderTop="1px solid" borderColor={c.border}>
                          <Flex alignItems="center" gap={2} mt={3}>
                            <Users size={16} color={c.accent} />
                            <Text tid="proveedores.wizard.step7.handlersTitle" fontSize="sm" fontWeight="700" color={c.text} />
                          </Flex>
                          {!isReadonly && (
                            <Flex gap={2} mt={3}>
                              <Box as="button" type="button" display="flex" alignItems="center" gap={1.5} px={3} py={1.5} border="1px solid" borderColor={c.border} color={c.text} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer" _hover={{ bg: c.bgHover }} onClick={downloadTemplate}>
                                <Download size={13} />{t('proveedores.wizard.step7.downloadTemplate')}
                              </Box>
                              <Box as="button" type="button" display="flex" alignItems="center" gap={1.5} px={3} py={1.5} border="1px solid" borderColor={c.border} color={c.text} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer" _hover={{ bg: c.bgHover }} onClick={() => { setBulkMrId(right.id); bulkInputRef.current?.click() }}>
                                <Upload size={13} />{t('proveedores.wizard.step7.bulkUpload')}
                              </Box>
                              <Box as="button" type="button" display="flex" alignItems="center" gap={1.5} px={3} py={1.5} bg={c.accent} color="white" borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer" _hover={{ opacity: 0.9 }} onClick={() => setHandlerModal({ mrId: right.id })}>
                                <Plus size={13} />{t('proveedores.wizard.step7.addHandler')}
                              </Box>
                            </Flex>
                          )}
                        </Flex>

                        {handlers.length > 0 ? (
                          <VStack gap={2} alignItems="stretch">
                            {handlers.map((h: ExplosiveHandlerResponse) => {
                              const sc = STATUS_COLORS[h.status] ?? STATUS_COLORS.unknown!
                              return (
                                <Flex key={h.id} px={3} py={2} bg={c.bgInput} borderRadius="lg" border="1px solid" borderColor={c.border} alignItems="center" justifyContent="space-between">
                                  <Grid templateColumns={{ base: '1fr', md: '2fr 1fr 1fr auto' }} gap={3} flex={1} alignItems="center">
                                    <Box>
                                      <Text fontSize="sm" fontWeight="600" color={c.text}>{h.fullName}</Text>
                                      <Text fontSize="xs" color={c.textMuted}>{h.identityDoc ?? '—'}</Text>
                                    </Box>
                                    <Text fontSize="sm" color={c.text}>{h.licenseNumber ?? '—'}</Text>
                                    <Text fontSize="sm" color={c.text}>{h.expirationDate ?? '—'}</Text>
                                    <Box px={2} py={0.5} borderRadius="full" bg={sc.bg} display="inline-flex">
                                      <Text fontSize="xs" fontWeight="600" color={sc.color}>{t(`proveedores.wizard.step7.status.${h.status}`)}</Text>
                                    </Box>
                                  </Grid>
                                  {!isReadonly && (
                                    <Box as="button" type="button" ml={3} p={1.5} color="red.500" borderRadius="md" cursor="pointer" _hover={{ bg: 'red.50' }} onClick={() => setDeleteHandler({ mrId: right.id, handlerId: h.id, name: h.fullName })}>
                                      <Trash2 size={14} />
                                    </Box>
                                  )}
                                </Flex>
                              )
                            })}
                          </VStack>
                        ) : (
                          <Text tid="proveedores.wizard.step7.noHandlers" fontSize="sm" color={c.textMuted} textAlign="center" py={3} />
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )
          })}
        </VStack>
      )}

      {/* Input oculto para carga masiva */}
      <input ref={bulkInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleBulkFile} />

      {authModal && (
        <AutorizacionModal
          key={authModal.editing?.id ?? 'new'}
          open
          onClose={() => setAuthModal(null)}
          miningRightName={savedMiningRights.find((r) => r.id === authModal.mrId)?.name}
          initial={authModal.editing}
          onSave={handleSaveAuth}
        />
      )}

      {handlerModal && companyId && (
        <ManipuladorModal
          open
          onClose={() => setHandlerModal(null)}
          onSave={handleSaveHandler}
          existingPersonIds={(savedMiningRights.find((r) => r.id === handlerModal.mrId)?.explosiveHandlers ?? []).map((h) => h.personId)}
        />
      )}

      <ConfirmDialog
        open={deleteAuth !== null}
        onClose={() => setDeleteAuth(null)}
        onConfirm={confirmDeleteAuth}
        title={t('proveedores.wizard.step7.deleteAuthTitle')}
        confirmLabel={t('common.delete')}
        variant="danger"
        isLoading={busy}
      >
        {t('proveedores.wizard.step7.deleteAuthConfirm', { name: deleteAuth?.name ?? '' })}
      </ConfirmDialog>

      <ConfirmDialog
        open={deleteHandler !== null}
        onClose={() => setDeleteHandler(null)}
        onConfirm={confirmDeleteHandler}
        title={t('proveedores.wizard.step7.deleteHandlerTitle')}
        confirmLabel={t('common.delete')}
        variant="danger"
        isLoading={busy}
      >
        {t('proveedores.wizard.step7.deleteHandlerConfirm', { name: deleteHandler?.name ?? '' })}
      </ConfirmDialog>
    </VStack>
  )
}
